import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Link,
  LinkOff,
  Refresh,
  Warning,
} from '@mui/icons-material';
import { ConnectedAccount } from '../../services/integrationsAPI';

export interface PlatformConfig {
  name: string;
  icon: React.ReactElement;
  color: string;
  description: string;
}

export type ConnectionState = 'connected' | 'not_connected' | 'needs_reauth';

export function getConnectionState(account: ConnectedAccount): ConnectionState {
  if (account.connected) return 'connected';
  if (account.needs_reauth) return 'needs_reauth';
  return 'not_connected';
}

interface IntegrationStatusCardProps {
  account: ConnectedAccount;
  config: PlatformConfig;
  isConnecting: boolean;
  isRefreshing: boolean;
  onConnect: (platform: string) => void;
  onDisconnect: (platform: string) => void;
  onRefresh: (platform: string) => void;
}

export const IntegrationStatusCard: React.FC<IntegrationStatusCardProps> = ({
  account,
  config,
  isConnecting,
  isRefreshing,
  onConnect,
  onDisconnect,
  onRefresh,
}) => {
  const state = getConnectionState(account);

  const chipProps = {
    connected: { label: 'Connected', color: 'success' as const, icon: <CheckCircle /> },
    needs_reauth: { label: 'Reconnect required', color: 'error' as const, icon: <ErrorIcon /> },
    not_connected: { label: 'Not connected', color: 'default' as const, icon: <Warning /> },
  }[state];

  const displayName =
    account.user_info?.name ||
    account.user_info?.username ||
    account.user_info?.email ||
    null;

  return (
    <Card
      sx={{
        height: '100%',
        borderLeft: 4,
        borderColor:
          state === 'connected'
            ? 'success.main'
            : state === 'needs_reauth'
              ? 'error.main'
              : 'grey.300',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: config.color, mr: 2 }}>{config.icon}</Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">{config.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {config.description}
            </Typography>
          </Box>
          <Chip
            label={chipProps.label}
            color={chipProps.color}
            size="small"
            icon={chipProps.icon}
          />
        </Box>

        {state === 'connected' && displayName && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Connected account
            </Typography>
            <Typography variant="body2">{displayName}</Typography>
            {account.user_info?.username && account.user_info?.name && (
              <Typography variant="caption" color="text.secondary">
                @{account.user_info.username}
              </Typography>
            )}
            {account.connected_at && (
              <Typography variant="caption" color="text.secondary" display="block">
                Connected {new Date(account.connected_at).toLocaleDateString()}
              </Typography>
            )}
            {account.last_validated && (
              <Typography variant="caption" color="text.secondary" display="block">
                Last checked {new Date(account.last_validated).toLocaleString()}
              </Typography>
            )}
          </Box>
        )}

        {state === 'needs_reauth' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {account.message || 'Your token expired. Reconnect to publish again.'}
          </Alert>
        )}

        {state === 'not_connected' && account.message && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {account.message}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {state === 'connected' ? (
            <>
              <Button
                variant="outlined"
                startIcon={<LinkOff />}
                onClick={() => onDisconnect(account.platform)}
                color="error"
                size="small"
              >
                Disconnect
              </Button>
              <Tooltip title="Re-check connection">
                <IconButton
                  onClick={() => onRefresh(account.platform)}
                  size="small"
                  disabled={isRefreshing}
                >
                  {isRefreshing ? <CircularProgress size={18} /> : <Refresh />}
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={isConnecting ? <CircularProgress size={16} color="inherit" /> : <Link />}
              onClick={() => onConnect(account.platform)}
              disabled={isConnecting}
              color={state === 'needs_reauth' ? 'error' : 'primary'}
              sx={
                state !== 'needs_reauth'
                  ? { bgcolor: config.color, '&:hover': { bgcolor: config.color } }
                  : undefined
              }
            >
              {isConnecting ? 'Connecting...' : state === 'needs_reauth' ? 'Reconnect' : 'Connect'}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
